import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  panel: {
    maxWidth: 300,
  },
  sectionTitle: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    fontSize: 9,
    letterSpacing: 0.4,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(0.5),
    marginTop: 2,
  },
  tile: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: '3px 6px',
    cursor: 'help',
  },
  tileLabel: {
    fontSize: 9,
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
  },
  tileValue: {
    fontWeight: 600,
    fontSize: 13,
  },
  tileUnit: {
    fontSize: 9,
    color: theme.palette.text.secondary,
    marginLeft: 2,
  },
  bssTile: {
    gridColumn: '1 / -1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  bssChip: {
    fontWeight: 600,
    fontSize: 10,
    height: 18,
  },
}));

// Laid out row-major on a 2-column grid so the left column reads RMSE (final →
// last 6 months → simulation mean) and the right one bias (final → simulation mean).
const MODEL_METRICS = ['rmseFinal', 'biasFinal', 'rmseLast6m', 'biasAvg', 'rmseAvg'];
const MEASURED_METRICS = ['rmsm', 'biasm'];

const BSS_LEVEL_COLORS = {
  bad: '#d32f2f',
  poor: '#f57c00',
  reasonable: '#fbc02d',
  good: '#7cb342',
  excellent: '#388e3c',
};

// Qualitative BSS classification (van Rijn et al., 2003).
const classifyBss = (bss) => {
  if (!Number.isFinite(bss)) return null;
  if (bss < 0) return 'bad';
  if (bss < 0.1) return 'poor';
  if (bss < 0.3) return 'reasonable';
  if (bss < 0.6) return 'good';
  return 'excellent';
};

const formatMetric = (v) => (Number.isFinite(v) ? v.toFixed(2) : '—');

/**
 * @function ShoreSMetricsPanel
 * @description Compact dashboard for `state.shoreSAnalysis.errorMetrics`:
 * the model-error metrics and the measured coastline change as labeled tiles
 * (value + unit, hover tooltip explaining each one), and the Brier Skill
 * Score highlighted with a qualitative colored chip.
 * **Called by:
 * @see {ShoreSAnalysisSteps}
 * @memberof module:ShoreS
 * @returns {JSX.Element|null} The metrics panel, or null before a validation run completes.
 */
const ShoreSMetricsPanel = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const errorMetrics = useSelector((s) => s.shoreSAnalysis.errorMetrics);

  if (!errorMetrics) return null;

  const tile = (key) => (
    
      <Box className={classes.tile}>
        <Typography className={classes.tileLabel}>
          {t(`forms.shoreS.analysis.metrics.${key}.label`)}
        </Typography>
        <Typography className={classes.tileValue} component="span">
          {formatMetric(errorMetrics[key])}
        </Typography>
        {Number.isFinite(errorMetrics[key]) && (
          <Typography className={classes.tileUnit} component="span">
            {t('forms.shoreS.analysis.metrics.unit')}
          </Typography>
        )}
      </Box>
    
  );

  const bssLevel = classifyBss(errorMetrics.bss);

  return (
    <Box className={classes.panel}>
      <Typography className={classes.sectionTitle}>
        {t('forms.shoreS.analysis.metrics.modelSection')}
      </Typography>
      <Box className={classes.grid}>{MODEL_METRICS.map(tile)}</Box>

      <Typography className={classes.sectionTitle}>
        {t('forms.shoreS.analysis.metrics.measuredSection')}
      </Typography>
      <Box className={classes.grid}>
        {MEASURED_METRICS.map(tile)}

      
          <Box className={`${classes.tile} ${classes.bssTile}`}>
            <Box>
              <Typography className={classes.tileLabel}>
                {t('forms.shoreS.analysis.metrics.bss.label')}
              </Typography>
              <Typography className={classes.tileValue} component="span">
                {formatMetric(errorMetrics.bss)}
              </Typography>
            </Box>
            
          </Box>
       
      </Box>
    </Box>
  );
};

export default ShoreSMetricsPanel;
